// Days of the week (ISO 8601: 1 = Monday, 7 = Sunday)
export type DayOfWeek = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export const DAY_OF_WEEK_LABELS: Record<DayOfWeek, string> = {
  1: 'Lundi',
  2: 'Mardi',
  3: 'Mercredi',
  4: 'Jeudi',
  5: 'Vendredi',
  6: 'Samedi',
  7: 'Dimanche',
};

// ===== ACTIVITY TYPES =====

/**
 * Activity types for merchant classification (type de commerce)
 */
export type ActivityType =
  | 'rotisseur'
  | 'boulanger'
  | 'patissier'
  | 'boucher'
  | 'charcutier'
  | 'fromager'
  | 'apiculteur'
  | 'maraicher'
  | 'poissonnier'
  | 'traiteur'
  | 'epicier'
  | 'caviste'
  | 'autre';

/**
 * Display labels for activity types (French)
 */
export const ACTIVITY_TYPE_LABELS: Record<ActivityType, string> = {
  rotisseur: 'Rôtisseur',
  boulanger: 'Boulanger',
  patissier: 'Pâtissier',
  boucher: 'Boucher',
  charcutier: 'Charcutier',
  fromager: 'Fromager',
  apiculteur: 'Apiculteur',
  maraicher: 'Maraîcher',
  poissonnier: 'Poissonnier',
  traiteur: 'Traiteur',
  epicier: 'Épicier',
  caviste: 'Caviste',
  autre: 'Autre',
};

/**
 * Format activity types for display
 * @param types Array of activity types
 * @returns Formatted string with middle dot separator (e.g., "Boucher · Charcutier")
 */
export function formatActivityTypes(types: ActivityType[]): string {
  if (!types || types.length === 0) return '';
  return types.map(t => ACTIVITY_TYPE_LABELS[t]).join(' · ');
}

/**
 * Tenant info for market display (minimal projection)
 */
export interface TenantWithMarketInfo {
  id: string;
  name: string;
  slug: string;
  activityTypes: ActivityType[];
  description?: string;
  phoneNumber?: string;
  profilePhoto?: string;
  isAbsent?: boolean;
}

export interface Market {
  id: string;
  city: string;                    // Nom de la ville
  dayOfWeek: DayOfWeek;            // Jour du marché (ISO 8601: 1-7)
  openingTime: string;             // Heure d'ouverture (HH:mm)
  closingTime: string;             // Heure de fermeture (HH:mm)
  description?: string;            // Description du marché
  address: string;                 // Adresse exacte
  picture?: string;                // URL de la photo
  isActive: boolean;               // Marché actif ou non
  createdAt: Date;
  updatedAt: Date;
}

export interface TenantMarket {
  id: string;
  tenantId: string;                // ID du commerçant
  marketId: string;                // ID du marché
  orderStartTime?: string;         // Heure début commandes (HH:mm), si différent du marché
  orderEndTime?: string;           // Heure fin commandes (HH:mm), si différent du marché
  orderDeadlineHours?: number;     // Heures avant le marché pour fermer les commandes (ex: 24 = veille)
  isActive: boolean;               // Association active ou non
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Choice for select/multiselect options
 */
export interface SelectChoice {
  id: string;           // e.g., "fine", "medium", "coarse"
  label: string;        // e.g., "Fine", "Moyenne", "Grosse"
  priceModifier?: number; // Price adjustment in euros (e.g., +2 for premium option)
}

/**
 * Component of a composition option (reference to a product)
 * Used for composite products like macaron gift boxes
 */
export interface CompositionComponent {
  productId: string;    // Reference to product (e.g., "vanilla-macaron")
  label: string;        // Display label (cached from product name for quick display)
}

/**
 * Selection entry for composition options in order lines
 */
export interface CompositionSelection {
  productId: string;    // Reference to selected product
  quantity: number;     // Quantity of this item in the composition
}

export interface ProductOption {
  id: string;           // e.g., "withoutSauce", "grindSize", "composition"
  label: string;        // e.g., "sans sauce", "Mouture", "Composition"
  type: 'boolean' | 'select' | 'multiselect' | 'composition';
  choices?: SelectChoice[];           // Required for select/multiselect
  maxSelections?: number;             // Optional limit for multiselect
  required?: boolean;                 // For 'select' type: true = mandatory, false/undefined = optional
  components?: CompositionComponent[]; // Required for composition type
  totalRequired?: number;             // Required total for composition (e.g., 6, 12, 24)
}

/**
 * Portion size for products sold in different formats
 * e.g., coffee in 250g, 500g, 1kg bags
 */
export interface PortionSize {
  id: string;            // Unique identifier (e.g., "250g", "small")
  label: string;         // Display label (e.g., "250g", "Petit")
  price: number;         // Price for this portion
}

// ===== SALE MODE SYSTEM =====

/**
 * Sale mode defines how a product is sold and priced
 */
export type SaleMode =
  | 'UNIT'                    // Pièce prix fixe (tarte, bouteille)
  | 'UNIT_PRICED_BY_WEIGHT'   // Pièce facturée au poids (poulet rôti)
  | 'WEIGHT'                  // Poids libre (olives)
  | 'WEIGHT_ESTIMATED'        // Poids par personnes (rôti de bœuf)
  | 'WEIGHT_PACKAGED';        // Formats fixes (café 250g/500g/1kg)

/**
 * Weight estimate for products sold by unit but priced by weight
 * e.g., whole chicken sold at €15/kg with average weight 1.2kg
 */
export interface WeightEstimate {
  averageWeight: number;                        // Poids moyen en kg
  servesPerUnit?: { min: number; max: number }; // Portions par unité (e.g., 3-4 personnes)
}

/**
 * Serving estimate for products sold by weight with serving suggestions
 * e.g., roast beef at €32/kg with 200g per person
 * Customer enters the number of persons they want.
 */
export interface ServingEstimate {
  gramsPerPerson: number;                       // Grammes par personne
}

export interface OrderLineOption {
  id: string;
  value: boolean | string | string[] | CompositionSelection[];
  // boolean                → type 'boolean'
  // string                 → type 'select' (choice id)
  // string[]               → type 'multiselect' (choice ids)
  // CompositionSelection[] → type 'composition' (product selections with quantities)
}

export interface Product {
  id: string;
  name: string;                           // Customer-facing full name
  shortenName?: string;                   // Compact merchant display
  description?: string;                   // Customer-facing description
  picture?: string;                       // Legacy URL to product image (external URL)
  imageUrl?: string;                      // Firebase Storage URL for uploaded image
  thumbnailUrl?: string;                  // Firebase Storage URL for thumbnail
  imageUpdatedAt?: string;                // ISO timestamp of last image update
  section?: string;                       // Customer-facing section (e.g., "Volailles", "Accompagnements")
  subSection?: string;                    // Customer-facing sub-section within a section (e.g., "Vert", "Noir" for tea)
  notes?: string;                         // Internal merchant notes
  displayOrder?: number;                  // Order within section for display
  tenantId?: string;                      // Multi-tenant support
  outOfStockDate?: string;                // YYYY-MM-DD - product is out of stock if matches today
  isActive?: boolean;                     // false = seasonal/disabled product (hidden from customers)
  marketIds?: string[];                   // IDs des marchés où le produit est disponible (vide = tous)

  // ===== PRICING =====
  /**
   * Sale mode - defines how the product is sold and priced.
   */
  saleMode: SaleMode;

  // Prix (interprétation selon saleMode)
  // - UNIT: prix par unité (€)
  // - UNIT_PRICED_BY_WEIGHT: prix par kg (€/kg)
  // - WEIGHT*: prix par kg (€/kg)
  price?: number;

  // ===== MODE-SPECIFIC =====
  // Pour UNIT_PRICED_BY_WEIGHT
  weightEstimate?: WeightEstimate;

  // Pour WEIGHT_ESTIMATED
  servingEstimate?: ServingEstimate;

  // Pour WEIGHT_PACKAGED (existant)
  portionSizes?: PortionSize[];           // Predefined portion sizes (e.g., 250g, 500g, 1kg)

  // ===== COMMON =====
  disclaimer?: string;                    // Message affiché au client (e.g., "Prix final selon poids réel")
  availableOptions?: ProductOption[];     // Dynamic customization options
}

export interface Section {
  id: string;
  name: string;
  displayOrder: number;
}

export interface OrderLine {
  productId: string;
  quantity: number;
  preparedQuantity: number;
  options?: OrderLineOption[];            // Dynamic customization options
  portionSizeId?: string;                 // Selected portion size (e.g., "250g", "small")

  // ===== SALE MODE FIELDS (Phase 5) =====
  saleMode?: SaleMode;                    // Sale mode at time of order (snapshot)
  servings?: number;                      // Number of servings (for WEIGHT_ESTIMATED)
  estimatedWeight?: number;               // Estimated weight in kg (for UNIT_PRICED_BY_WEIGHT, WEIGHT_ESTIMATED)
  estimatedPrice?: number;                // Estimated price in € (for variable price modes)
  finalWeight?: number;                   // Final weight after weighing (set by merchant)
  finalPrice?: number;                    // Final price after weighing (set by merchant)
}

export type OrderStatus = 'awaiting_validation' | 'pending' | 'ready' | 'done';

export type OrderSource = 'customer' | 'merchant';

export interface Order {
  id: string;
  customerName: string;
  phoneNumber?: string;                   // Customer phone number (digits only)
  pickupAtISO: string;
  pickupDate: string;
  lines: OrderLine[];
  status: OrderStatus;
  paid: boolean;
  note?: string;
  fcmToken?: string;                      // Firebase Cloud Messaging token for push notifications
  createdAtISO: string;
  updatedAtISO: string;
  tenantId?: string;                      // Multi-tenant support
  marketId?: string;                      // Market where order will be picked up
  customerId?: string;                    // Anonymous Firebase Auth UID for customer order tracking
  source?: OrderSource;                   // Origin of the order (customer app or merchant app)
  recurringOrderId?: string;              // ID of the RecurringOrder template that generated this order
}

// ===== RECURRING ORDERS =====

export type RecurringOrderStatus = 'active' | 'paused';

/**
 * A line in a recurring order template (no prepared quantity, no sale mode snapshot)
 */
export interface RecurringOrderLine {
  productId: string;
  quantity: number;
  options?: OrderLineOption[];
  portionSizeId?: string;
}

/**
 * A recurring order template created by the merchant for a regular customer.
 * Each week, the Cloud Function generates a real Order from this template.
 */
export interface RecurringOrder {
  id: string;
  tenantId: string;
  marketId: string;
  customerName: string;
  phoneNumber?: string;
  preferredPickupTime: string;            // "HH:mm"
  lines: RecurringOrderLine[];
  status: RecurringOrderStatus;
  note?: string;
  createdAtISO: string;
  updatedAtISO: string;
  lastGeneratedDate?: string;             // YYYY-MM-DD — date of the last generated order
}

export function derivePickupDate(pickupAtISO: string): string {
  const date = new Date(pickupAtISO);
  // Format as YYYY-MM-DD in LOCAL timezone to match user's day
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get active options from an order line (boolean options only)
 * @param line Order line
 * @returns Array of active boolean option IDs
 */
export function getActiveOptions(line: OrderLine): string[] {
  return (line.options || [])
    .filter(opt => opt.value === true)
    .map(opt => opt.id);
}

/**
 * Check if an order line has a specific boolean option enabled
 * @param line Order line
 * @param optionId Option ID to check
 * @returns true if option is enabled
 */
export function hasOption(line: OrderLine, optionId: string): boolean {
  return line.options?.some(opt => opt.id === optionId && opt.value === true) ?? false;
}

/**
 * Get option value from an order line
 * @param line Order line
 * @param optionId Option ID
 * @returns The option value (boolean, string, string[], or CompositionSelection[]) or undefined
 */
export function getOptionValue(line: OrderLine, optionId: string): boolean | string | string[] | CompositionSelection[] | undefined {
  return line.options?.find(opt => opt.id === optionId)?.value;
}

/**
 * Get option labels for an order line based on product's available options
 * Handles boolean, select, and multiselect types (composition is handled separately)
 * @param line Order line
 * @param product Product
 * @returns Array of option labels
 */
export function getOptionLabels(line: OrderLine, product: Product): string[] {
  if (!line.options || !product.availableOptions) return [];

  const labels: string[] = [];

  for (const lineOpt of line.options) {
    const productOpt = product.availableOptions.find(opt => opt.id === lineOpt.id);
    if (!productOpt) continue;

    // Skip composition options (handled separately via formatCompositionDisplay)
    if (productOpt.type === 'composition') continue;

    if (productOpt.type === 'boolean' && lineOpt.value === true) {
      // Boolean option: show the option label
      labels.push(productOpt.label);
    } else if (productOpt.type === 'select' && typeof lineOpt.value === 'string') {
      // Select option: show "Label: Choice"
      const choice = productOpt.choices?.find(c => c.id === lineOpt.value);
      if (choice) {
        labels.push(`${productOpt.label}: ${choice.label}`);
      }
    } else if (productOpt.type === 'multiselect' && Array.isArray(lineOpt.value) && !isCompositionSelection(lineOpt.value)) {
      // Multiselect option: show "Label: Choice1, Choice2"
      const choiceLabels = (lineOpt.value as string[])
        .map(choiceId => productOpt.choices?.find(c => c.id === choiceId)?.label)
        .filter((label): label is string => Boolean(label));
      if (choiceLabels.length > 0) {
        labels.push(`${productOpt.label}: ${choiceLabels.join(', ')}`);
      }
    }
  }

  return labels;
}

/**
 * Get the price for a specific portion size
 * @param product The product
 * @param portionSizeId The portion size ID (optional)
 * @returns The unit price for this portion, or null if no price available
 */
export function getPortionPrice(product: Product, portionSizeId?: string): number | null {
  // If no portion size specified or product has no portion sizes, return base price
  if (!portionSizeId || !product.portionSizes?.length) {
    return product.price ?? null;
  }

  const portion = product.portionSizes.find(p => p.id === portionSizeId);
  if (!portion) {
    return product.price ?? null; // Fallback to base price if portion not found
  }

  return portion.price;
}

/**
 * Get the portion size object for a product
 * @param product The product
 * @param portionSizeId The portion size ID
 * @returns The portion size object or undefined
 */
export function getPortionSize(product: Product, portionSizeId?: string): PortionSize | undefined {
  if (!portionSizeId || !product.portionSizes?.length) return undefined;
  return product.portionSizes.find(p => p.id === portionSizeId);
}

/**
 * Calculate total price modifier from selected options in an order line
 * @param line Order line with options
 * @param product Product with available options definition
 * @returns Total price modifier in euros
 */
export function getOptionsPriceModifier(line: OrderLine, product: Product | undefined): number {
  if (!line.options || !product?.availableOptions) return 0;

  let total = 0;

  for (const lineOption of line.options) {
    const productOption = product.availableOptions.find(o => o.id === lineOption.id);
    if (!productOption || !productOption.choices) continue;

    if (productOption.type === 'select' && typeof lineOption.value === 'string') {
      const choice = productOption.choices.find(c => c.id === lineOption.value);
      if (choice?.priceModifier) {
        total += choice.priceModifier;
      }
    } else if (productOption.type === 'multiselect' && Array.isArray(lineOption.value)) {
      for (const choiceId of lineOption.value) {
        if (typeof choiceId === 'string') {
          const choice = productOption.choices.find(c => c.id === choiceId);
          if (choice?.priceModifier) {
            total += choice.priceModifier;
          }
        }
      }
    }
  }

  return total;
}

/**
 * Get the line price, prioritizing finalPrice > estimatedPrice > calculated price
 * Includes price modifiers from selected options
 * @param line Order line
 * @param product Product for this line
 * @returns The line total in euros, or null if not available
 */
export function getLinePrice(line: OrderLine, product: Product | undefined): number | null {
  // Priority 1: finalPrice (set by merchant after weighing)
  // Note: finalPrice already includes options modifiers
  if (line.finalPrice !== undefined) {
    return line.finalPrice;
  }

  // Priority 2: estimatedPrice (calculated at order time)
  // Note: estimatedPrice already includes options modifiers
  if (line.estimatedPrice !== undefined) {
    return line.estimatedPrice;
  }

  // Priority 3: Calculate from product price + options modifiers
  if (!product) return null;
  const unitPrice = getPortionPrice(product, line.portionSizeId);
  if (unitPrice === null) return null;
  const optionsModifier = getOptionsPriceModifier(line, product);
  // For unit-based modes, modifiers apply per unit: (unitPrice + modifier) × quantity
  // For weight-based modes, modifiers apply once per line: unitPrice × quantity + modifier
  const saleMode = line.saleMode ?? product.saleMode;
  if (isUnitCountSaleMode(saleMode)) {
    return (unitPrice + optionsModifier) * line.quantity;
  }
  return unitPrice * line.quantity + optionsModifier;
}

/**
 * Check if a line has variable pricing (estimated vs final)
 * @param line Order line
 * @returns true if the line has variable pricing
 */
export function isVariablePriceLine(line: OrderLine): boolean {
  const variableModes: SaleMode[] = ['UNIT_PRICED_BY_WEIGHT', 'WEIGHT', 'WEIGHT_ESTIMATED'];
  return line.saleMode !== undefined && variableModes.includes(line.saleMode);
}

/**
 * Check if a line has been weighed (final price set)
 * @param line Order line
 * @returns true if the line has been weighed
 */
export function isLineWeighed(line: OrderLine): boolean {
  return line.finalWeight !== undefined || line.finalPrice !== undefined;
}

/**
 * Calcule le total TTC d'une commande en euros
 * Uses finalPrice/estimatedPrice if available, otherwise calculates from product price
 * @param lines Les lignes de la commande
 * @param products La liste de tous les produits disponibles
 * @returns Le montant total TTC en euros, ou null si des produits n'ont pas de prix
 */
export function calculateOrderTotal(lines: OrderLine[], products: Product[]): number | null {
  let total = 0;
  let hasAllPrices = true;

  for (const line of lines) {
    const product = products.find(p => p.id === line.productId);
    const linePrice = getLinePrice(line, product);

    if (linePrice === null) {
      hasAllPrices = false;
      continue;
    }

    total += linePrice;
  }

  // Retourne null si au moins un produit n'a pas de prix
  return hasAllPrices && lines.length > 0 ? total : null;
}

/**
 * Check if a product is out of stock for a given date
 * @param product The product to check
 * @param date Optional date in YYYY-MM-DD format (defaults to today)
 * @returns true if product is out of stock for the given date
 */
export function isProductOutOfStock(product: Product, date?: string): boolean {
  if (!product.outOfStockDate) return false;
  if (date) {
    return product.outOfStockDate === date;
  }
  // Inline today's date calculation to avoid circular dependency with date-utils
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const today = `${year}-${month}-${day}`;
  return product.outOfStockDate === today;
}

/**
 * Check if a product is active (not disabled/seasonal)
 * @param product The product to check
 * @returns true if product is active (default), false if explicitly disabled
 */
export function isProductActive(product: Product): boolean {
  return product.isActive !== false;
}

/**
 * Safely format a number with fixed decimal places.
 * Returns fallback if number is NaN, Infinity, or not finite.
 * @param value The number to format
 * @param decimals Number of decimal places (default: 2)
 * @param fallback Value to return if number is invalid (default: "0.00")
 */
export function safeToFixed(value: number, decimals: number = 2, fallback?: string): string {
  if (!Number.isFinite(value)) {
    return fallback ?? (0).toFixed(decimals);
  }
  return value.toFixed(decimals);
}

/**
 * Format kg quantity: removes trailing zeros
 * 1.000 -> "1", 1.500 -> "1.5", 1.250 -> "1.25"
 * Returns "0" for invalid numbers (NaN, Infinity)
 */
export function formatKgQuantity(quantity: number): string {
  if (!Number.isFinite(quantity)) {
    return '0';
  }
  const fixed = quantity.toFixed(3);
  return parseFloat(fixed).toString();
}

/**
 * Format quantity display for order lines based on sale mode and portion size
 * - With portion size: "2× 250g"
 * - UNIT or UNIT_PRICED_BY_WEIGHT: "2×" (count)
 * - WEIGHT/WEIGHT_ESTIMATED: "1.5 kg" (uses finalWeight if available)
 *
 * @param quantity The quantity ordered
 * @param product The product (optional, for sale mode lookup)
 * @param portionSizeId The portion size ID (optional)
 * @param line The order line (optional, for finalWeight access)
 * @returns Formatted quantity string
 */
export function formatQuantityDisplay(
  quantity: number,
  product?: Product,
  portionSizeId?: string,
  line?: OrderLine
): string {
  const portionSize = product ? getPortionSize(product, portionSizeId) : undefined;

  // Use effective sale mode instead of just price_model
  const saleMode = product ? getEffectiveSaleMode(product) : undefined;
  const isWeightBased = saleMode === 'WEIGHT' || saleMode === 'WEIGHT_ESTIMATED';

  if (portionSize) {
    return `${quantity}× ${portionSize.label}`;
  } else if (isWeightBased) {
    // For weight-based products, prefer finalWeight if available
    const displayWeight = line?.finalWeight ?? quantity;
    return `${formatKgQuantity(displayWeight)} kg`;
  } else {
    // UNIT, UNIT_PRICED_BY_WEIGHT, WEIGHT_PACKAGED (without portionSizeId)
    return `${quantity}×`;
  }
}

/**
 * Type guard to check if a value is a CompositionSelection array
 */
export function isCompositionSelection(value: unknown): value is CompositionSelection[] {
  if (!Array.isArray(value) || value.length === 0) return false;
  const first = value[0];
  return typeof first === 'object' && first !== null && 'productId' in first && 'quantity' in first;
}

/**
 * Get composition selections from an order line option
 * @param line Order line
 * @param optionId Option ID to look for
 * @returns Array of composition selections or empty array
 */
export function getCompositionSelections(line: OrderLine, optionId: string): CompositionSelection[] {
  const option = line.options?.find(opt => opt.id === optionId);
  if (!option) return [];

  if (isCompositionSelection(option.value)) {
    return option.value;
  }
  return [];
}

/**
 * Get the first composition option from an order line (convenience function)
 * @param line Order line
 * @param product Product to find composition option
 * @returns Array of composition selections or empty array
 */
export function getLineCompositionSelections(line: OrderLine, product: Product): CompositionSelection[] {
  const compositionOption = product.availableOptions?.find(opt => opt.type === 'composition');
  if (!compositionOption) return [];
  return getCompositionSelections(line, compositionOption.id);
}

/**
 * Validate that composition selections sum to the required total
 * @param product Product with composition option
 * @param optionId Option ID
 * @param selections Current selections
 * @returns Validation result with total and required
 */
export function validateComposition(
  product: Product,
  optionId: string,
  selections: CompositionSelection[]
): { valid: boolean; total: number; required: number } {
  const option = product.availableOptions?.find(opt => opt.id === optionId);
  if (!option || option.type !== 'composition') {
    return { valid: false, total: 0, required: 0 };
  }

  const total = selections.reduce((sum, s) => sum + s.quantity, 0);
  const required = option.totalRequired || 0;

  return { valid: total === required, total, required };
}

/**
 * Format composition display for order lines
 * @param selections Composition selections
 * @param products Available products to look up names
 * @returns Formatted string like "3 Vanille, 2 Chocolat, 1 Framboise"
 */
export function formatCompositionDisplay(
  selections: CompositionSelection[],
  products: Product[]
): string {
  return selections
    .filter(s => s.quantity > 0)
    .map(s => {
      const product = products.find(p => p.id === s.productId);
      const name = product?.shortenName || product?.name || 'Inconnu';
      return `${s.quantity} ${name}`;
    })
    .join(', ');
}

/**
 * Check if a product has a composition option
 * @param product Product to check
 * @returns The composition option if found, undefined otherwise
 */
export function getCompositionOption(product: Product): ProductOption | undefined {
  return product.availableOptions?.find(opt => opt.type === 'composition');
}

// ===== TENANT ABSENCE SYSTEM =====

/**
 * Interface for tenant absence period checking
 */
export interface AbsencePeriod {
  absenceStart?: string;  // YYYY-MM-DD format (inclusive)
  absenceEnd?: string;    // YYYY-MM-DD format (inclusive)
}

/**
 * Check if a tenant is currently absent based on absence period
 * @param tenant Object with absenceStart and absenceEnd fields
 * @param date Optional date in YYYY-MM-DD format (defaults to today)
 * @returns true if tenant is absent for the given date
 */
export function isTenantAbsent(tenant: AbsencePeriod, date?: string): boolean {
  // If no absence dates set, not absent
  if (!tenant.absenceStart || !tenant.absenceEnd) {
    return false;
  }

  // Get today's date if not provided (inline to avoid circular dependency)
  const checkDate = date || (() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  })();

  // Compare dates as strings (YYYY-MM-DD format allows lexicographic comparison)
  return checkDate >= tenant.absenceStart && checkDate <= tenant.absenceEnd;
}

// ===== TENANT FEATURES =====

/**
 * Feature flags for tenant-specific beta features.
 * Add new features here as they are developed.
 * Remove features when they graduate to GA.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface TenantFeatures {
  // newProductFlow has graduated to GA and is now enabled for all tenants
  // Add future beta features here as key: boolean
}

// ===== TENANT TIER SYSTEM =====

/**
 * Tenant tier defines the subscription level
 * - free: Limited to 3 markets
 * - premium: Unlimited markets (contact for upgrade)
 */
export type TenantTier = 'free' | 'premium';

// ===== MARKET REQUEST SYSTEM =====

/**
 * Status of a market request
 */
export type MarketRequestStatus = 'pending' | 'approved' | 'rejected';

/**
 * Request for adding a new market to the system
 * Users can request markets that don't exist yet during onboarding or later
 */
export interface MarketRequest {
  id: string;
  requestedBy: string;            // userId
  requestedByEmail: string;       // Email for notification
  tenantId?: string;              // If request is made after onboarding
  city: string;                   // City name
  dayOfWeek: DayOfWeek;           // ISO 8601: 1-7
  status: MarketRequestStatus;
  createdAt: Date;
  reviewedAt?: Date;
  reviewedBy?: string;            // superadmin userId
  marketId?: string;              // Linked market after approval
}

// ===== SALE MODE HELPERS =====

/**
 * Check if a sale mode uses unit-based quantity (discrete count of items).
 * For these modes, price modifiers from options apply per unit.
 * For weight-based modes (WEIGHT, WEIGHT_ESTIMATED), modifiers apply once per line.
 */
export function isUnitCountSaleMode(saleMode: SaleMode): boolean {
  return saleMode === 'UNIT' || saleMode === 'UNIT_PRICED_BY_WEIGHT' || saleMode === 'WEIGHT_PACKAGED';
}

/**
 * Returns the sale mode for a product.
 * All products now have saleMode set (required field after migration).
 *
 * @param product The product to get sale mode for
 * @returns The product's SaleMode
 */
export function getEffectiveSaleMode(product: Product): SaleMode {
  return product.saleMode;
}

// ===== PRODUCT-MARKET AVAILABILITY =====

/**
 * Check if a product is available at a specific market
 * Products without marketIds are available at all markets (backward compatible)
 * @param product The product to check
 * @param marketId The market ID (if undefined, product is considered available)
 * @returns true if product is available at the given market
 */
export function isProductAvailableAtMarket(product: Product, marketId?: string | null): boolean {
  if (!marketId) return true;
  if (!product.marketIds || product.marketIds.length === 0) return true;
  return product.marketIds.includes(marketId);
}

// ===== ORDER DEADLINE SYSTEM =====

/**
 * Calculate the order deadline datetime for a market
 * @param market The market (for dayOfWeek and openingTime)
 * @param tenantMarket The tenant-market association (for orderDeadlineHours)
 * @param pickupDate The pickup date in YYYY-MM-DD format
 * @returns Date object representing the deadline, or null if no deadline configured
 */
export function getOrderDeadline(
  market: Market,
  tenantMarket: TenantMarket,
  pickupDate: string
): Date | null {
  if (!tenantMarket.orderDeadlineHours) return null;

  const [year, month, day] = pickupDate.split('-').map(Number);
  const { openingTime } = market;
  const [openH, openM] = openingTime.split(':').map(Number);

  // Deadline = market opening time on pickup day - orderDeadlineHours
  const marketOpening = new Date(year, month - 1, day, openH, openM, 0, 0);
  const deadline = new Date(marketOpening.getTime() - tenantMarket.orderDeadlineHours * 60 * 60 * 1000);

  return deadline;
}

/**
 * Check if ordering is still open (deadline not passed)
 * @param market The market
 * @param tenantMarket The tenant-market association
 * @param pickupDate The pickup date in YYYY-MM-DD format
 * @returns true if ordering is still possible, or if no deadline is configured
 */
export function isOrderingOpen(
  market: Market,
  tenantMarket: TenantMarket,
  pickupDate: string
): boolean {
  const deadline = getOrderDeadline(market, tenantMarket, pickupDate);
  if (!deadline) return true;
  return new Date() < deadline;
}

/**
 * Format the order deadline for display
 * @param deadline The deadline Date
 * @returns Human-readable string like "vendredi 14 février à 08h00"
 */
export function formatOrderDeadline(deadline: Date): string {
  const dateStr = deadline.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
  const hours = deadline.getHours().toString().padStart(2, '0');
  const minutes = deadline.getMinutes().toString().padStart(2, '0');
  return `${dateStr} à ${hours}h${minutes}`;
}
